<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { DateTime } from 'luxon';
import { useAlerts } from '@/plugins/alerts';
import { useMixins } from '@/plugins/mixins';
import { useLocalization } from '@/plugins/localization';
import AssistantService, { AssistantItemModel, AssistantRequestDataModel, AssistantChatItemModel, AssistantThreadModel, AssistantListItemModel } from '@/modules/ai/common/services/AssistantService';
import { Form, FormType } from '@/helpers/Form';
import { reactive, watch } from 'vue';
import { Option } from '@/helpers/Interfaces';
import { useSignalR } from '@/plugins/signalr';
import { useAssistantChatStore } from '@/store/assistantChat';
import { useAiSettingsStore } from '@/modules/ai/settings/store/index';
import { useMedia } from '@/plugins/media';
import AiService from '@/modules/core/common/services/AiService';
import AiImage from '@/assets/img/ai/ai-image.png';

const rwd = useMedia();

const { $alert } = useAlerts();
const { $handleException, $filters } = useMixins();
const { $t } = useLocalization();
const { assistantChatData } = useAssistantChatStore();
const { aiSettings } = useAiSettingsStore();

const { channel } = useSignalR();
const server = channel('runassistant');

const total = ref(0);
const displayTotal = computed<string>(() => total.value > 99 ? '99+' : total.value.toString());

const chat = ref<AssistantChatItemModel[]>([]);
const chatLength = computed(() => chat.value.length);

const assistantOptions = ref<Option<string>[]>([]);
const threadOptions = ref<Option<string>[]>([]);
const threads = ref<AssistantThreadModel[]>([]);
const assistantName = ref<string>('');

const isCreateThread = ref<boolean>(false);
const isModifyThread = ref<boolean>(false);

const sending = ref<boolean>(false);

const assistantForm = ref<FormType<AssistantRequestDataModel>>(Form.create<AssistantRequestDataModel>({
    threadId: '',
    assistantId: '',
    messageContent: ''
}));

const form = reactive(Form.create<AssistantItemModel>({
    id: '',
    utcDateTime: null,
    name: '',
    model: '',
    instructions: '',
    tools: [],
    fileIds: [],
    functions: [],
    metadata: {}
})) as FormType<AssistantItemModel>;

const threadForm = ref<FormType<AssistantThreadModel>>(Form.create<AssistantThreadModel>({
    threadId: '',
    threadName: '',
    isMainThread: false,
    createdAt: null
}));

const read = async (message: AssistantChatItemModel): Promise<void> =>
{
    if (total.value > 0)
    {
        total.value--;
    }
};

server.on.toolStatusReceived = async (payload: any): Promise<void> =>
{
    if (snapshotThreadId.value === payload.snapshotThreadId)
    {
        total.value++;
        messagesLoadData(threadId.value);
    }
};

server.on.AssistantMessageReceived = async (payload: any): Promise<void> =>
{
    if (snapshotThreadId.value === payload.snapshot)
    {
        const existingMessage = chat.value.find(item => item.id === payload.messageId);

        if (existingMessage)
        {
            // Aktualizacjia wiadomości o nowe tokeny - czat
            existingMessage.content[0].text += payload.token;
            existingMessage.content[0].type = 'text';
        }
        else
        {
            // Dodanie wiadomości asystenta z pierwszym tokenem - czat
            newChatAssistantItem(payload);
        }
    }
};

onMounted(async () =>
{
    aiSettingsLoadData();
    assistantsLoadData();
    threadsLoadData();
});

function newChatUserItem(userMessage: string): void
{
    const newChatUserItem: AssistantChatItemModel = {
        id: 'msg_abc',
        utcDateTime: DateTime.utc(),
        role: 'user',
        content: [{
            text: userMessage,
            type: 'text'
        }]
    };

    chat.value.unshift(newChatUserItem);
}

function newChatAssistantItem(payload: any): void
{
    const newChatAssistantItem: AssistantChatItemModel = {
        id: payload.messageId,
        utcDateTime: payload.utcDateTime,
        role: payload.role,
        content: [{
            text: payload.token,
            type: 'text'
        }]
    };

    chat.value.unshift(newChatAssistantItem);
}

async function sendMessage(): Promise<void>
{
    try
    {
        sending.value = true;

        form.wait();

        if (assistantForm.value.messageContent)
        {
            // Dodanie wiadomości użytkownika - czat
            newChatUserItem(assistantForm.value.messageContent);

            if (threadForm.value.threadId === '')
            {
                $alert.warning($t('[[[Wybierz wątek w którym chcesz prowadzić konwersację.]]]'));
            }

            assistantForm.value.threadId = threadForm.value.threadId;

            // Utworzenie wiadomości
            await AssistantService.CreateAssistantMessageAsync(assistantForm.value);

            // Uruchomienie biegu asystenta
            await AssistantService.AssistantRunAsync(assistantForm.value);

            assistantForm.value.messageContent = '';
        }
    }
    catch (ex)
    {
        $handleException(ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
    finally
    {
        form.continue();

        sending.value = false;
    }
}

async function threadsLoadData(): Promise<void>
{
    try
    {
        threadOptions.value = [];

        const response = await AssistantService.GetAssistantThreadsAsync();

        if (response.length === 0)
        {
            // $alert.warning("Brak dostępnych wątków do pobrania, utwórz nowy wątek.");
            isCreateThread.value = true;
        }
        else
        {
            threads.value = response;

            selectMainThread(response);

            // Załaduj dane z wybranego wątku
            findThreadById(threadForm.value.threadId);

            messagesLoadData(threadForm.value.threadId);
        }
    }
    catch (ex)
    {
        // $handleException(ex, {
        //     400: (ex: any) => $alert.warning(ex.message)
        // });
    }

    isModifyThread.value = true;
}

async function assistantsLoadData(): Promise<void>
{
    try
    {
        const assistants = await AssistantService.GetAssistantsAsync();

        assistants.forEach(option =>
        {
            assistantOptions.value.push({ value: option.id, text: $t('[[[' + option.name + ']]]') });
        });

        isMainAssistantBoolean(assistants);
    }
    catch (ex)
    {
        // $handleException(ex, {
        //     400: (ex: any) => $alert.warning(ex.message)
        // });
    }
}

function isMainAssistantBoolean(assistants: AssistantListItemModel[]): void
{
    for (const assistant of assistants)
    {
        const processed: Record<string, any> = { ...assistant.metadata };

        if ('isMainAssistant' in processed)
        {
            if (processed.isMainAssistant === 'true')
            {
                assistantForm.value.assistantId = assistant.id;

                assistantName.value = assistant.name;

                assistantChatData.isMainAssistant = true;
            }
        }
    }

    if (!assistantForm.value.assistantId)
    {
        assistantForm.value.assistantId = assistants[0].id;

        assistantName.value = assistants[0].name;
    }
}

async function selectMainThread(response: AssistantThreadModel[]): Promise<void>
{
    const mainThread = response.find(thread => thread.isMainThread);

    if (mainThread)
    {
        // Wybierz najnowszy główny wątek
        threadForm.value.threadId = mainThread.threadId;
    }
    else
    {
        // Wybierz najnowszy wątek, gdy brak głównego wątku
        threadForm.value.threadId = response[0].threadId;
    }
}

function findThreadById(threadId: string): void
{
    const thread = threads.value.find(thread => thread.threadId === threadId);

    threadForm.value.threadId = thread.threadId;
    threadForm.value.isMainThread = thread.isMainThread;
    threadForm.value.threadName = thread.threadName;
    threadForm.value.createdAt = thread.createdAt;
}

async function messagesLoadData(threadId: string): Promise<boolean>
{
    try
    {
        if (!threadId)
        {
            // $alert.warning('Wybierz wątek');

            return false;
        }

        const responseMessages = await AssistantService.GetAssistantMessagesAsync(threadId);

        chat.value = responseMessages;
    }
    catch (ex)
    {
        // $handleException(ex, {
        //     400: (ex: any) => $alert.warning($t('[[[Wystąpił problem z pobraniem wiadomości.]]]'))
        // });

        return false;
    }

    return true;
}

const threadId = computed(() =>
{
    if(assistantChatData.threadId)
    {
        return assistantChatData.threadId;
    }
    else
    {
        return threadForm.value.threadId;
    }
});

const snapshotThreadId = computed<string>(() =>
{
    const prefix = 'thread_';

    if (threadId.value.startsWith(prefix))
    {
        const part1 = threadId.value.substring(prefix.length, prefix.length + 3);
        const part2 = threadId.value.substring(threadId.value.length - 3);

        return `${prefix}${part1}_${part2}`;
    }

    return '';
});

const assistantChatDataThreadId = computed(() => assistantChatData.threadId);

watch(assistantChatDataThreadId, async () =>
{
    await threadsLoadData();
});

const assistantId = computed(() => assistantChatData.assistantId);

watch(assistantId, async () =>
{
    assistantsLoadData();
});

const aiEnabled = computed(() => aiSettings.enabled);

watch(aiEnabled, async () =>
{
    if (aiEnabled.value)
    {
        assistantsLoadData();
        threadsLoadData();
    }
});

const aiSettingsLoadData = async (): Promise<void> =>
{
    try
    {
        const settings = await AiService.isEnabled();

        // Zapis ustawień do store
        aiSettings.enabled = settings.enabled;
        aiSettings.toolsAi = settings.toolsAi;
        aiSettings.assistantAi = settings.assistantAi;
    }
    catch (ex)
    {
        $handleException(ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
};
</script>

<template>
    <ideo-dropdown v-if="aiEnabled" ref="dropdownRef" class="assistant-chat-dropdown ai-enabled" variant="link" no-caret right>
        <template #button-content>
            <span class="position-relative">
                <i class="fa-regular fa-robot"></i>
                <span class="badge text-bg-danger position-absolute top-0 start-100 translate-middle ms-2" v-if="total > 0">{{ displayTotal }}</span>
            </span>
        </template>
        <template v-if="!aiEnabled && rwd.desktop()">
            <div>
                <img class="w-100" :src="AiImage" alt="..." />

                <div class="p-3">
                    <div class="fs-5 mb-1 fw-bolder">
                        {{ $t('[[[Wybróbuj możliwości AI]]]') }}
                    </div>

                    <div class="fs-6 mb-3">
                        {{ $t('[[[Przejdź do konfiguracji, aby włączyć narzędzia]]]') }}
                        <b>{{ $t('[[[Asystenta AI]]]') }}</b>.
                    </div>

                    <action-button variant="fancy" icon="far fa-microchip-ai" :text="$t('[[[Konfiguracja asystenta ai]]]')" :to="{name:'core-ai-settings'}" />
                </div>
            </div>
        </template>
        <template v-if="aiEnabled && assistantChatData.isMainAssistant">
            <ideo-form-group class="pt-2">
                <div class="bg-body chat-container scroll" ref="chatContainer">
                    <div
                        v-for="item in chat"
                        :key="item.id"
                        class="chat-item"
                        @mouseover="read(item)"
                    >
                        <div :class="item.role === 'assistant' ? 'mb-1' : 'mb-0'"><strong>{{ item.role === 'Assistant' ? assistantName : '' }}</strong></div>
                        <p v-for="content in item.content" :key="content.text">{{ content.text }}</p>
                        <div class="font-color"><i>{{ $filters.datetime(item.utcDateTime) }}</i></div>
                    </div>
                    <div v-show="chatLength === 0" class="font-color">
                        {{ $t('[[[Wątek nie zawiera jeszcze żadnych wiadomości]]]') }}
                    </div>
                </div>
            </ideo-form-group>

            <!-- Wiadomość -->
            <ideo-form-group>
                <ideo-form-textarea v-model="assistantForm.messageContent" name="messageContent" rows="2" :placeholder="$t('[[[Zapytaj asystenta]]]')"></ideo-form-textarea>
            </ideo-form-group>

            <!-- Wyślij wiadomość -->
            <div class="pb-2">
                <action-button
                    variant="fancy"
                    icon="far fa-paper-plane"
                    class="p-20 w-100"
                    @click.prevent="sendMessage()"
                    :disabled="!form.active() || !assistantForm.messageContent"
                    :loading="sending"
                    :text="$t('[[[Wyślij wiadomość]]]')"
                />
            </div>
        </template>
        <template v-else>
            <div>
                <div class="p-3">
                    <div class="fs-6 mb-3">
                        {{ $t('[[[Aby móc korzystać z czatu, wybierz asystenta z listy i oznacz go jako głównego asystenta panelu.]]]') }}
                    </div>

                    <action-button variant="fancy" icon="far fa-microchip-ai" :text="$t('[[[Lista asystentów]]]')" :to="{name:'core-ai-assistants'}" />
                </div>
            </div>
        </template>
    </ideo-dropdown>
</template>

<style lang="scss">
.assistant-chat-dropdown {
    font-size: 20px;
    .dropdown-menu {
        //position: fixed !important;
        //transform: none !important;
        //inset: auto 32px 78px auto !important
    }
    .dropdown {
        &-menu {
            padding: 0.5rem 1rem;
            width: 300px;
            z-index: 10001;
            max-height: calc(100vh - 65px);
            overflow: auto;
        }
        &-toggle {
            i.fa-robot {
                font-size: 1.125rem;
                color: var(--bs-navbar-color);
            }
        }
        &-item {
            white-space: unset;
            padding: 0.625rem;
            border-radius: 0.313rem;
            background: var(--bs-tertiary-bg);
            display: flex;
            align-items: center;

            &:not(:last-child) {
                margin-bottom: 0.5rem;
            }
            &:hover {
                background: var(--bs-secondary-bg);
            }
            i {
                font-size: 1rem;
            }
            .title, .date {
                display: block;
                color: var(--bs-body-color);
            }
            .title {
                margin-bottom: 0.625rem;
            }
        }
        .show-all {
            text-transform: unset;
            font-size: 0.875rem;
            font-weight: 400;
        }
    }
}
.form {
    margin-top: 1rem;

    &:first-child {
        margin-top: 0;
    }
}
.page-pills-options > button {
    &:first-of-type {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
    }

    &:last-of-type {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
    }
}
.chat-container {
    display: flex;
    gap: 12px;
    height:232px;
    overflow-y: auto;
    display: flex;
    flex-direction: column-reverse;
    padding-right: 8px;
    padding-top: 2px;
    padding-bottom: 2px;
}
.chat-item {
    background-color: var(--bs-tertiary-bg);
    padding: 10px 15px;
    border-radius: 5px;
    transition: background-color 0.3s ease;
}
.chat-item:hover {
    background-color: var(--bs-secondary-bg);
}
.font-color {
    color: #cfcfcf;
    font-size: 0.8em;
}
</style>
